/**
 * Bulk Discount Calculator — SUPAEASY-mirrored, variant-metafield driven
 *
 * Mirrors the SUPAEASY product-discount function's tier structure on the PDP.
 *
 * Reads JSON config (emitted by snippets/bulk-discount-display.liquid) containing:
 *   - currentVariantId
 *   - variants: { [variantId]: { price, available, group } }
 *       group is the string key (e.g. "PB1") from
 *       variant.metafields.custom.price_family_id_from_p21
 *   - tierTables: { [group]: [{qty, percentage}, ...] }  (from shop metafield mirror)
 *       qty is the cumulative threshold — matches SUPAEASY's discountConfig shape.
 *   - debug: whether merchant has enabled debug mode on the block
 *
 * User-facing strings come from block data attributes:
 *   - data-save-label-format: e.g. "Save {percentage}%"
 *   - data-each-label: e.g. " each"
 *
 * Display: derives ranges from successive qty thresholds. Given tiers
 * [{qty:2,%:10}, {qty:4,%:20}, {qty:6,%:30}, {qty:8,%:35}] the UI shows:
 *   "2-3 @ 10%", "4-5 @ 20%", "6-7 @ 30%", "8+ @ 35%"
 *
 * Re-hydrates on Enterprise's on:variant:change CustomEvent (dispatched by
 * <variant-picker>) and re-fetches cart on on:cart:update / on:cart:add /
 * on:cart:remove / on:cart:refresh. Hides itself if the variant has no group
 * or the group has no tier data.
 */

(() => {
  const HIDDEN_CLASS = 'bulk-discount--hidden';
  const CART_EVENTS = ['on:cart:update', 'on:cart:add', 'on:cart:remove', 'on:cart:refresh'];

  class BulkDiscountCalculator {
    constructor(container) {
      this.container = container;
      this.tiersContainer = this.container.querySelector('.bulk-discount__tiers');
      // Scope queries and listeners to the enclosing Shopify section so multiple
      // product sections on one page (Main Product + Featured Product, etc.) don't
      // cross-talk. Falls back to the container's parent — never document — because
      // a document-scoped listener would respond to variant changes from any product
      // on the page and swap us to a variant we don't have config for.
      this.sectionScope = this.container.closest('[id^="shopify-section-"]') || this.container.parentElement;

      const configElement = this.container.querySelector('#bulk-discount-config');
      if (!configElement) {
        console.error('[BulkDiscount] Config element not found');
        return;
      }

      try {
        this.config = JSON.parse(configElement.textContent);
      } catch (error) {
        console.error('[BulkDiscount] Config parse error:', error);
        return;
      }

      this.saveLabelFormat = this.container.dataset.saveLabelFormat || 'Save {percentage}%';
      this.eachLabel = this.container.dataset.eachLabel ?? ' each';
      this.debugMode = this.config.debug === true;

      this.currentVariantId = String(this.config.currentVariantId);
      this.cartData = null;
      this.activeTiers = [];
      this.variantChangeHandler = null;
      this.cartEventHandler = null;

      // Tier tables don't change during session lifetime. Normalise keys once so
      // variant switches only read the already-normalised map, never rebuild it.
      this.normalisedTierTables = BulkDiscountCalculator.normaliseKeys(this.config.tierTables);

      this.init();
    }

    /**
     * Canonical group form is uppercase, no whitespace (e.g. "PB1", "PB4"). Metafield
     * values from feeds or legacy data may carry stray spaces ("PB 4"); the shop tier
     * metafield may carry matching-but-dirty keys. Normalise both sides on lookup so a
     * dirty input never silently hides the tier table.
     */
    static normaliseGroup(value) {
      return String(value ?? '').trim().replace(/\s+/g, '');
    }

    static normaliseKeys(obj) {
      return Object.fromEntries(
        Object.entries(obj || {}).map(([k, v]) => [
          BulkDiscountCalculator.normaliseGroup(k),
          v,
        ])
      );
    }

    /**
     * Always read the current qty from the DOM rather than caching. Themes sometimes
     * reset the qty input programmatically on variant change without dispatching
     * input/change events, so a cached value goes stale and the active-tier highlight
     * sticks on the old variant's qty.
     */
    getCurrentQuantity() {
      const qtyInput = this.sectionScope.querySelector('quantity-input input[name="quantity"]');
      if (!qtyInput) return 1;
      const parsed = parseInt(qtyInput.value, 10);
      return Number.isFinite(parsed) && parsed >= 1 ? parsed : 1;
    }

    async init() {
      this.resolveTiersForCurrentVariant();
      if (this.activeTiers.length === 0) {
        this.hideComponent();
        return;
      }

      await this.fetchCart();
      this.setupQuantityListener();
      this.setupEventListeners();
      this.calculateAndDisplay();
    }

    resolveTiersForCurrentVariant() {
      const variant = this.config.variants?.[this.currentVariantId];
      const groupKey = BulkDiscountCalculator.normaliseGroup(variant?.group);
      if (!variant || !groupKey) {
        this.activeTiers = [];
        return;
      }

      const primary = this.normalisedTierTables[groupKey];
      if (Array.isArray(primary) && primary.length > 0) {
        this.activeTiers = this.normaliseTiers(primary);
        return;
      }

      if (this.debugMode) {
        console.warn(`[BulkDiscount] No tier data for group ${groupKey}`);
      }
      this.activeTiers = [];
    }

    /**
     * Sort by qty ascending and ensure numeric types. Matches SUPAEASY's "find highest
     * applicable tier" logic: iterate tiers and pick the highest qty threshold <= the
     * total quantity.
     */
    normaliseTiers(tiers) {
      return [...tiers]
        .map((t) => ({ qty: Number(t.qty), percentage: Number(t.percentage) }))
        .filter((t) => Number.isFinite(t.qty) && Number.isFinite(t.percentage))
        .sort((a, b) => a.qty - b.qty);
    }

    getCurrentVariant() {
      return this.config.variants?.[this.currentVariantId];
    }

    hideComponent() {
      this.container?.classList.add(HIDDEN_CLASS);
    }

    showComponent() {
      this.container?.classList.remove(HIDDEN_CLASS);
    }

    async fetchCart() {
      try {
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`);
        if (response.ok) {
          this.cartData = await response.json();
        }
      } catch (error) {
        console.error('[BulkDiscount] Cart fetch failed:', error);
      }
    }

    setupQuantityListener() {
      const qtyInput = this.sectionScope.querySelector('quantity-input input[name="quantity"]');
      if (!qtyInput) return;

      const onQtyChange = () => this.calculateAndDisplay();
      qtyInput.addEventListener('change', onQtyChange);
      qtyInput.addEventListener('input', onQtyChange);
    }

    /**
     * Enterprise theme event shape:
     *   - <variant-picker> dispatches on:variant:change (bubbling) with detail.variant.
     *     Listener is scoped to the enclosing Shopify section so multiple product
     *     sections on one page don't cross-talk.
     *   - Cart mutations dispatch on:cart:add / on:cart:update / on:cart:remove /
     *     on:cart:refresh at document level from cart-drawer / cart-items /
     *     catalogue-block. Cart state is page-global, so document scope is correct.
     *
     * variant-picker may fire with a null variant.id for invalid option combinations;
     * reconcile against the hidden form input in that case so the component can hide
     * gracefully rather than holding onto a stale variant.
     */
    setupEventListeners() {
      this.variantChangeHandler = (event) => {
        const newVariant = event.detail?.variant;
        if (newVariant?.id) {
          this.onVariantChange(String(newVariant.id));
          return;
        }

        const hiddenId = this.sectionScope.querySelector('input[name="id"]')?.value?.trim();
        if (hiddenId && hiddenId !== this.currentVariantId) {
          this.onVariantChange(hiddenId);
        } else if (!hiddenId) {
          this.hideComponent();
        }
      };
      this.sectionScope.addEventListener('on:variant:change', this.variantChangeHandler);

      this.cartEventHandler = () => {
        this.fetchCart().then(() => this.calculateAndDisplay());
      };
      CART_EVENTS.forEach((eventName) => {
        document.addEventListener(eventName, this.cartEventHandler);
      });
    }

    onVariantChange(newVariantId) {
      this.currentVariantId = newVariantId;
      this.resolveTiersForCurrentVariant();

      if (this.activeTiers.length === 0) {
        this.hideComponent();
        return;
      }

      this.calculateAndDisplay();
    }

    calculateAndDisplay() {
      if (!this.activeTiers || this.activeTiers.length === 0) {
        this.hideComponent();
        return;
      }

      const cartQuantity = this.getCartQuantityForCurrentVariant();
      const totalQuantity = cartQuantity + this.getCurrentQuantity();
      const applicableTier = this.findApplicableTier(totalQuantity);

      this.renderTiers(applicableTier);
      this.showComponent();
    }

    /**
     * SUPAEASY's discount function iterates cart.lines and uses line.quantity alone —
     * no aggregation across lines sharing the same metafield group. The PDP display
     * must match that scope, otherwise two variants carrying the same group will
     * appear to bump each other's tier on the PDP while the cart applies per-line.
     */
    getCartQuantityForCurrentVariant() {
      if (!this.cartData || !Array.isArray(this.cartData.items)) return 0;

      return this.cartData.items.reduce((sum, item) => {
        if (String(item.variant_id) === this.currentVariantId) {
          return sum + item.quantity;
        }
        return sum;
      }, 0);
    }

    /**
     * SUPAEASY pattern: highest qty threshold the total quantity has reached.
     * Tiers are pre-sorted ascending by qty in normaliseTiers(), so walk from the end.
     */
    findApplicableTier(quantity) {
      for (let i = this.activeTiers.length - 1; i >= 0; i--) {
        if (quantity >= this.activeTiers[i].qty) return this.activeTiers[i];
      }
      return null;
    }

    /**
     * Built with DOM methods (createElement + textContent) rather than innerHTML so
     * no dynamic value is interpreted as markup. Taxable variants render both Inc and
     * Ex VAT prices and rely on the body-class toggle for visibility (mirrors
     * snippets/price.liquid). Non-taxable variants render a single exempt price group
     * so the displayed figure can't flicker between Inc/Ex on toggle.
     */
    renderTiers(applicableTier) {
      if (!this.tiersContainer) return;

      const variant = this.getCurrentVariant();
      const basePriceExCents = variant?.price ?? 0;
      const isTaxable = variant?.taxable !== false;
      const vatMultiplier = isTaxable ? (Number(this.config.vatRate) || 1) : 1;
      // Round to whole cents before any tier calculation so float drift
      // (e.g. 299 * 1.2 = 358.80000000000007) can't compound into percentage math.
      const basePriceIncCents = Math.round(basePriceExCents * vatMultiplier);

      // Container class drives subtitle visibility (Inc/Ex vs No VAT) via CSS.
      this.container.classList.toggle('bulk-discount--vat-exempt', !isTaxable);

      this.tiersContainer.replaceChildren();

      this.activeTiers.forEach((tier, index) => {
        const isActive = applicableTier && tier.qty === applicableTier.qty;

        const li = document.createElement('li');
        li.className = 'discount-tier';
        if (isActive) li.classList.add('discount-tier--active');

        const qtyWrapper = document.createElement('div');
        qtyWrapper.className = 'discount-tier__quantity-wrapper';
        const qtySpan = document.createElement('span');
        qtySpan.className = 'discount-tier__quantity';
        qtySpan.textContent = this.formatQuantityRange(index);
        qtyWrapper.appendChild(qtySpan);

        const savings = document.createElement('div');
        savings.className = 'discount-tier__savings';

        const priceWrapper = document.createElement('span');
        priceWrapper.className = 'discount-tier__price';

        if (isTaxable) {
          priceWrapper.appendChild(
            this.buildPriceGroup('vat--include', this.calculateTierPrice(basePriceIncCents, tier.percentage))
          );
          priceWrapper.appendChild(
            this.buildPriceGroup('vat--exclude', this.calculateTierPrice(basePriceExCents, tier.percentage))
          );
        } else {
          priceWrapper.appendChild(
            this.buildPriceGroup('vat--exempt', this.calculateTierPrice(basePriceExCents, tier.percentage))
          );
        }

        const badge = document.createElement('span');
        badge.className = 'discount-tier__badge';
        badge.textContent = this.saveLabelFormat.replace('{percentage}', tier.percentage);

        savings.appendChild(priceWrapper);
        savings.appendChild(badge);

        li.appendChild(qtyWrapper);
        li.appendChild(savings);

        this.tiersContainer.appendChild(li);
      });
    }

    /**
     * VAT-scoped wrapper with amount + suffix as siblings. Keeping them siblings
     * (rather than nesting suffix inside amount) lets em-based font-sizing stay
     * predictable: both inherit from the same parent font-size.
     */
    buildPriceGroup(vatClass, formattedAmount) {
      const group = document.createElement('span');
      group.className = `discount-tier__price-group ${vatClass}`;

      const amount = document.createElement('span');
      amount.className = 'discount-tier__price-amount';
      amount.textContent = formattedAmount;

      const suffix = document.createElement('span');
      suffix.className = 'discount-tier__price-suffix';
      suffix.textContent = this.eachLabel;

      group.appendChild(amount);
      group.appendChild(suffix);

      return group;
    }

    /**
     * Cumulative qty thresholds become display ranges. The last tier is open-ended ("N+").
     * Intermediate tiers cap at (nextTier.qty - 1).
     */
    formatQuantityRange(index) {
      const current = this.activeTiers[index];
      const next = this.activeTiers[index + 1];
      if (!next) return `${current.qty}+`;
      if (next.qty === current.qty + 1) return `${current.qty}`;
      return `${current.qty}-${next.qty - 1}`;
    }

    calculateTierPrice(basePriceCents, percentage) {
      // Round the discount to show the correct price.
      const discountCents = Math.round((basePriceCents * percentage) / 100);
      return this.formatMoney(basePriceCents - discountCents);
    }

    /**
     * Delegate to Shopify's own formatMoney when available (handles every placeholder
     * tag and thousands/decimal separators correctly). Fall back to a substitution
     * that at least respects the no-decimals placeholder variant so JPY / HUF / etc.
     * format strings render the right precision. Most Shopify themes ship
     * option_selection.js on product pages, so the fallback rarely fires in practice.
     */
    formatMoney(cents) {
      if (typeof window.Shopify?.formatMoney === 'function') {
        return window.Shopify.formatMoney(cents, this.config.moneyFormat);
      }
      const format = this.config.moneyFormat || '{{amount}}';
      const amount = (cents / 100).toFixed(2);
      const amountNoDecimals = Math.round(cents / 100).toString();
      return format.replace(/\{\{\s*([\w.]+)\s*\}\}/g, (_, placeholder) => {
        return placeholder.includes('no_decimals') ? amountNoDecimals : amount;
      });
    }

    /**
     * Tear down listeners so the calculator doesn't leak when Shopify's Theme Editor
     * re-renders the section via AJAX (shopify:section:unload fires first, then
     * :section:load re-runs the init which creates a fresh instance).
     */
    destroy() {
      if (this.variantChangeHandler) {
        this.sectionScope?.removeEventListener('on:variant:change', this.variantChangeHandler);
      }
      if (this.cartEventHandler) {
        CART_EVENTS.forEach((eventName) => {
          document.removeEventListener(eventName, this.cartEventHandler);
        });
      }
    }
  }

  /* ── Lifecycle ──
   *
   * Track instances per container so Shopify's Theme Editor AJAX re-renders work
   * cleanly: shopify:section:unload destroys the old instance, shopify:section:load
   * initialises the new one. Idempotency flag prevents double-init on repeated
   * shopify:section:load events for the same element.
   */
  const instances = new WeakMap();

  const initBulkDiscount = (root = document) => {
    root.querySelectorAll('.bulk-discount').forEach((container) => {
      if (instances.has(container)) return;
      instances.set(container, new BulkDiscountCalculator(container));
    });
  };

  const destroyBulkDiscount = (root) => {
    root.querySelectorAll?.('.bulk-discount').forEach((container) => {
      const instance = instances.get(container);
      if (instance) {
        instance.destroy();
        instances.delete(container);
      }
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initBulkDiscount());
  } else {
    initBulkDiscount();
  }

  // Shopify Theme Editor re-renders sections via AJAX on setting changes. Without
  // these handlers the block would stop responding to variant/cart events until a
  // full page reload.
  document.addEventListener('shopify:section:load', (event) => initBulkDiscount(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroyBulkDiscount(event.target));
})();
